<template>
  <div class="game-trend-mobile">
    <!-- 图表容器 -->
    <div class="chart-card">
      <div class="card-header">
        <span class="card-title">比赛类型趋势</span>
        <div class="card-actions">
          <el-select
            v-model="gameTypes"
            multiple
            collapse-tags
            placeholder="选择比赛类型"
            class="header-select type-select"
            @change="handleTypeChange"
          >
            <div class="select-actions">
              <el-button size="mini" type="text" @click.stop.prevent="handleSelectAll">全选</el-button>
              <el-divider direction="vertical" />
              <el-button size="mini" type="text" @click.stop.prevent="clearTypeFilter">重置</el-button>
            </div>
            <el-option
              v-for="item in gameTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <el-select
            v-model="timeRange"
            placeholder="选择时间范围"
            class="header-select time-select"
            @change="handleTimeRangeChange"
          >
            <el-option label="全部" value="all" />
            <el-option label="最近一个月" value="1m" />
            <el-option label="最近三个月" value="3m" />
            <el-option label="最近半年" value="6m" />
            <el-option label="最近一年" value="1y" />
          </el-select>
          <el-button type="text" icon="el-icon-download" @click="handleExport" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
        </div>
      </div>
      <div class="chart-container" ref="chart"></div>
    </div>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="95%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
    >
      <div slot="title" class="dialog-header">
        <span>比赛类型趋势</span>
        <div class="dialog-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          <el-button type="text" icon="el-icon-close" @click="closeDialog" />
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import _ from 'lodash'

export default {
  name: 'GameTrendChartMobile',
  data() {
    return {
      chart: null,
      chartData: {
        dates: [],
        scores: [],
        ranks: [],
        totals: [],
        names: [],
        times: [],
        gameTypes: []
      },
      gameTypes: [],
      gameTypeOptions: [],
      timeRange: 'all',
      fullscreenChart: null,
      dialogVisible: false,
      resizeHandler: null,
      isInitializing: false,
      typeColors: {},
      loading: false
    }
  },
  mounted() {
    this.initChart()
    this.fetchGameTypes()
    this.fetchData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
    if (this.fullscreenChart) {
      this.fullscreenChart.dispose()
    }
    window.removeEventListener('resize', this.resizeChart)
  },
  watch: {
    dialogVisible(val) {
      if (!val && this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    }
  },
  methods: {
    dataURLToBlob(dataURL) {
      const arr = dataURL.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) u8arr[n] = bstr.charCodeAt(n)
      return new Blob([u8arr], { type: mime })
    },
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = filename
      link.href = url
      document.body.appendChild(link)
      link.click()
      URL.revokeObjectURL(url)
      document.body.removeChild(link)
    },
    initChart() {
      this.chart = echarts.init(this.$refs.chart)
    },
    resizeChart() {
      this.chart?.resize()
    },
    async fetchData() {
      try {
        this.loading = true
        
        console.log('开始获取数据...')
        console.log('当前时间范围:', this.timeRange)
        console.log('当前选中类型:', this.gameTypes)
        
        // 构建基本参数
        const params = {}
        
        // 添加赛事类型参数
        if (this.gameTypes && this.gameTypes.length > 0) {
          params.game_types = this.gameTypes
        }
        
        // 添加日期范围参数
        if (this.timeRange && this.timeRange !== 'all') {
          const end = new Date()
          const start = new Date()
          
          switch (this.timeRange) {
            case '1m':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              break
            case '3m':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              break
            case '6m':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 180)
              break
            case '1y':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 365)
              break
          }
          
          params.start_date = start.toISOString().split('T')[0]
          params.end_date = end.toISOString().split('T')[0]
          
          console.log('添加日期范围参数:', params.start_date, '-', params.end_date)
        }
        
        console.log('请求参数:', JSON.stringify(params, null, 2))
        
        // 发送请求
        const res = await request.get('/api/analysis/game/trend', { params })
        
        console.log('API Response:', res)
        
        // 获取原始数据
        let rawData = null
        if (res.code === 200 && res.data) {
          rawData = res.data
        } else if (res.data) {
          rawData = res.data
        } else {
          console.error('API返回异常数据:', res)
          this.$message.error('获取数据失败')
          return
        }
        
        // 如果设置了时间范围，在前端进行过滤
        if (this.timeRange && this.timeRange !== 'all' && rawData.dates && rawData.dates.length > 0) {
          console.log('在前端根据时间范围过滤数据:', this.timeRange)
          
          const end = new Date()
          const start = new Date()
          
          switch (this.timeRange) {
            case '1m':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              break
            case '3m':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              break
            case '6m':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 180)
              break
            case '1y':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 365)
              break
          }
          
          // 过滤符合时间范围的数据
          const filteredIndices = []
          rawData.dates.forEach((dateStr, index) => {
            const currentDate = new Date(dateStr)
            if (currentDate >= start && currentDate <= end) {
              filteredIndices.push(index)
            }
          })
          
          console.log('符合时间范围的索引:', filteredIndices)
          
          // 根据过滤后的索引创建新的数据集
          const filteredData = {
            dates: [],
            scores: [],
            ranks: [],
            totals: [],
            names: [],
            times: [],
            game_types: []
          }
          
          filteredIndices.forEach(index => {
            filteredData.dates.push(rawData.dates[index])
            filteredData.scores.push(rawData.scores[index])
            filteredData.ranks.push(rawData.ranks[index])
            filteredData.totals.push(rawData.totals[index])
            filteredData.names.push(rawData.names[index])
            filteredData.times.push(rawData.dates[index])
            filteredData.game_types.push(rawData.game_types[index])
          })
          
          // 使用过滤后的数据
          this.chartData = {
            dates: filteredData.dates || [],
            scores: filteredData.scores || [],
            ranks: filteredData.ranks || [],
            totals: filteredData.totals || [],
            names: filteredData.names || [],
            times: filteredData.dates || [],
            gameTypes: filteredData.game_types || []
          }
          
          console.log('前端过滤后的数据:', this.chartData)
        } else {
          // 使用原始数据
          this.chartData = {
            dates: rawData.dates || [],
            scores: rawData.scores || [],
            ranks: rawData.ranks || [],
            totals: rawData.totals || [],
            names: rawData.names || [],
            times: rawData.dates || [],
            gameTypes: rawData.game_types || []
          }
        }
        
        this.updateChart()
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    },
    async fetchGameTypes() {
      try {
        const res = await request.get('/api/dict/list', {
          params: {
            dict_key: 'game_type'
          }
        })
        if (res.data && Array.isArray(res.data)) {
          const colors = [
            '#3498DB', '#E74C3C', '#2ECC71', '#9B59B6', '#F1C40F',
            '#1ABC9C', '#E67E22', '#34495E', '#C0392B', '#27AE60',
            '#8E44AD', '#F39C12', '#16A085', '#D35400', '#7F8C8D'
          ]
          
          this.typeColors = {}
          res.data.forEach((item, index) => {
            this.typeColors[item.dict_value] = colors[index % colors.length]
          })

          this.gameTypeOptions = res.data.map(item => ({
            value: item.dict_value,
            label: item.dict_value
          }))
          this.gameTypes = this.gameTypeOptions.map(type => type.value)
        } else {
          console.error('获取赛事类型数据格式错误:', res)
          this.$message.error('获取赛事类型数据格式错误')
        }
      } catch (error) {
        console.error('获取赛事类型失败:', error)
        this.$message.error('获取赛事类型失败')
        this.gameTypeOptions = []
        this.gameTypes = []
      }
    },
    formatRank(rank, total) {
      if (rank === 0 && total === 0) return '未添加'
      if (!rank && rank !== 0) return null
      if (rank < 0.01) return 'Top 0.01%'
      return rank.toFixed(2) + '%'
    },
    formatScore(score) {
      return score.toFixed(2) + '%'
    },
    updateChart() {
      // 如果没有选择任何类型，清空图表
      if (this.gameTypes.length === 0) {
        const emptyOption = {
          backgroundColor: '#ffffff',
          grid: {
            top: '10%',
            left: '5%',
            right: '5%',
            bottom: '10%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: []
          },
          yAxis: [
            {
              type: 'value',
              name: '得分率',
              min: 0,
              max: 100,
              position: 'left',
              nameLocation: 'end',
              nameGap: 15,
              nameTextStyle: {
                align: 'right',
                verticalAlign: 'bottom',
                padding: [0, 20, 0, 0],
                fontSize: 12
              },
              axisLabel: {
                formatter: '{value}%',
                fontSize: 10
              }
            },
            {
              type: 'value',
              name: '排位 Top',
              min: 0,
              max: 100,
              position: 'right',
              inverse: true,
              nameLocation: 'start',
              nameGap: 15,
              nameTextStyle: {
                align: 'left',
                verticalAlign: 'bottom',
                padding: [0, 0, 0, 15],
                fontSize: 12
              },
              axisLabel: {
                formatter: '{value}%',
                fontSize: 10
              }
            }
          ],
          series: []
        }
        this.chart.setOption(emptyOption, true)
        return
      }

      const validScores = this.chartData.scores.filter(score => score !== null && score !== undefined)
      const validRanks = this.chartData.ranks.filter((rank, index) => {
        const total = this.chartData.totals[index]
        return rank !== null && rank !== undefined && !(rank === 0 && total === 0)
      })
      
      const averageScore = validScores.length > 0 
        ? validScores.reduce((sum, score) => sum + score, 0) / validScores.length 
        : 0
      
      const averageRank = validRanks.length > 0
        ? validRanks.reduce((sum, rank) => sum + rank, 0) / validRanks.length
        : 0

      const option = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          confine: true,
          enterable: false,
          hideDelay: 100,
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 11
          },
          formatter: function(params) {
            if (!params || !Array.isArray(params) || params.length === 0) return ''
            const index = params[0].dataIndex
            const time = this.chartData.times[index] || ''
            const name = this.chartData.names[index] || ''
            const title = name || time
            const gameType = this.chartData.gameTypes?.[index]
            
            // 获取赛事类型标签样式
            const getGameTypeTagStyle = (type) => {
              if (!type || !this.typeColors[type]) return null
              const color = this.typeColors[type]
              // 将颜色转换为 rgba 格式用于背景
              const hexToRgba = (hex, alpha) => {
                const r = parseInt(hex.slice(1, 3), 16)
                const g = parseInt(hex.slice(3, 5), 16)
                const b = parseInt(hex.slice(5, 7), 16)
                return `rgba(${r}, ${g}, ${b}, ${alpha})`
              }
              const bg = hexToRgba(color, 0.2)
              return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 10px; background-color: ${bg}; border: 1px solid ${color}; color: ${color}; line-height: 1.2;`
            }
            
            let content = ''
            // 如果有赛事类型，显示带颜色的标签
            if (gameType) {
              const tagStyle = getGameTypeTagStyle(gameType)
              if (tagStyle) {
                content += `<div><span style="${tagStyle}">${gameType}</span></div>`
              } else {
                content += `<div>赛事类型：${gameType}</div>`
              }
            }
            if (time && name) {
              content += `<div>时间：${time}</div>`
            }
            params.forEach(param => {
              if (param.seriesName === '得分率' && param.value !== null && param.value !== undefined) {
                content += `<div>${param.seriesName}：${this.formatScore(param.value)}</div>`
              } else if (param.seriesName === '排位') {
                const rank = this.chartData.ranks[index]
                const total = this.chartData.totals[index]
                if (rank === 0 && total === 0) {
                  content += `<div>${param.seriesName}：未添加</div>`
                } else {
                  content += `<div>${param.seriesName}：Top ${rank.toFixed(2)}%</div>`
                  const actualRank = Math.round(rank * total / 100)
                  content += `<div>排名：${actualRank}/${total}</div>`
                }
              }
            })
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${title}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  ${content}
                </div>
              </div>
            `
          }.bind(this)
        },
        grid: {
          top: '10%',
          left: '5%',
          right: '5%',
          bottom: '10%',
          containLabel: true
        },
        legend: {
          data: [
            '得分率',
            {
              name: '平均得分率',
              itemStyle: { opacity: 0 },
              lineStyle: {
                type: 'dashed',
                width: 1,
                color: '#409EFF'
              }
            },
            '排位',
            {
              name: '平均排位',
              itemStyle: { opacity: 0 },
              lineStyle: {
                type: 'dashed',
                width: 1,
                color: '#67C23A'
              }
            }
          ],
          bottom: '0%',
          left: 'center',
          itemGap: 20,
          itemWidth: 15,
          itemHeight: 10,
          textStyle: {
            fontSize: 10,
            color: '#666'
          }
        },
        xAxis: {
          type: 'category',
          data: this.chartData.dates,
          axisLabel: {
            show: true,
            interval: (index, value) => {
              const total = this.chartData.dates.length
              if (total <= 3) return true
              
              if (index === 0 || index === total - 1) return true
              
              const step = Math.floor(total / 3)
              return index === step || index === 2 * step
            },
            formatter: (value) => {
              const date = new Date(value)
              return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`
            },
            fontSize: 10,
            rotate: 45
          },
          axisTick: {
            show: true,
            interval: (index, value) => {
              const total = this.chartData.dates.length
              if (total <= 3) return true
              
              if (index === 0 || index === total - 1) return true
              
              const step = Math.floor(total / 3)
              return index === step || index === 2 * step
            }
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '得分率',
            min: 0,
            max: 100,
            position: 'left',
            nameLocation: 'start',
            nameGap: 25,
            nameTextStyle: {
              align: 'right',
              verticalAlign: 'top',
              padding: [0, 10, 0, 0],
              color: '#666',
              fontSize: 10
            },
            axisLabel: {
              formatter: '{value}%',
              fontSize: 10
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
                color: '#E4E7ED'
              }
            },
            axisLine: {
              show: false
            }
          },
          {
            type: 'value',
            name: '排位 Top',
            min: 0,
            max: 100,
            position: 'right',
            inverse: true,
            nameLocation: 'end',
            nameGap: 25,
            nameTextStyle: {
              align: 'left',
              verticalAlign: 'bottom',
              padding: [0, 0, 0, 0],
              color: '#666',
              fontSize: 10
            },
            axisLabel: {
              formatter: (value) => {
                if (value === 0) {
                  return '0%'
                }
                return Math.round(value) + '%'
              },
              fontSize: 10
            },
            splitLine: {
              show: false
            },
            axisLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: '得分率',
            type: 'bar',
            data: this.chartData.scores.map((score, index) => {
              const gameType = this.chartData.gameTypes[index]
              const color = this.typeColors[gameType]
              return {
                value: score,
                itemStyle: {
                  color: color || '#409EFF',
                  borderRadius: [2, 2, 0, 0]
                }
              }
            }),
            yAxisIndex: 0,
            barMaxWidth: 25,
            barGap: '15%'
          },
          {
            name: '排位',
            type: 'line',
            smooth: true,
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: {
              color: '#5470c6',
              borderWidth: 1
            },
            lineStyle: {
              width: 2,
              color: '#5470c6'
            },
            data: this.chartData.ranks.map((rank, index) => {
              const total = this.chartData.totals[index]
              const isSpecial = rank === 0 && total === 0
              const actualRank = Math.round(rank * total / 100)
              
              return {
                value: actualRank === 1 ? 0 : rank,
                itemStyle: {
                  color: actualRank === 1 ? '#67C23A' : (isSpecial ? '#ff4d4f' : '#5470c6')
                }
              }
            }),
            yAxisIndex: 1,
            connectNulls: true
          },
          {
            name: '平均得分率',
            type: 'line',
            smooth: true,
            showSymbol: false,
            lineStyle: {
              type: 'dashed',
              width: 1,
              color: '#409EFF'
            },
            data: new Array(this.chartData.dates.length).fill(averageScore)
          },
          {
            name: '平均排位',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            showSymbol: false,
            lineStyle: {
              type: 'dashed',
              width: 1,
              color: '#67C23A'
            },
            data: new Array(this.chartData.dates.length).fill(averageRank)
          }
        ]
      }

      this.chart.setOption(option, true)
    },
    handleExport() {
      const dpr = window.devicePixelRatio || 1
      const pixelRatio = Math.min(3, dpr * 1.5)
      const dataURL = this.chart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
      const blob = this.dataURLToBlob(dataURL)
      this.downloadBlob(blob, 'game_type_trend.png')
    },
    handleDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.fullscreenChart
          if (!chartDom) return

          this.fullscreenChart = echarts.init(chartDom)
          const option = this.chart.getOption()
          
          // 全屏时调整配置
          option.grid = {
            left: '5%',
            right: '5%',
            bottom: '10%',
            top: '10%',
            containLabel: true
          }

          this.fullscreenChart.setOption(option)
          
          this.resizeHandler = _.debounce(() => {
            if (this.fullscreenChart) {
              this.fullscreenChart.resize()
            }
          }, 100)

          window.addEventListener('resize', this.resizeHandler)
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleDialogClosed() {
      this.isInitializing = false
    },
    handleFullscreen() {
      this.dialogVisible = true
    },
    closeDialog() {
      this.dialogVisible = false
    },
    handleExportFullscreen() {
      if (!this.fullscreenChart) return
      
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.fullscreenChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'game_type_trend.png')
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    handleTypeChange() {
      this.fetchData()
    },
    handleTimeRangeChange() {
      this.fetchData()
    },
    handleSelectAll() {
      this.gameTypes = this.gameTypeOptions.map(type => type.value)
      this.fetchData()
    },
    handleReset() {
      this.gameTypes = this.gameTypeOptions.map(type => type.value)
      this.timeRange = 'all'
      this.fetchData()
    },
    clearTypeFilter() {
      this.gameTypes = []
      this.fetchData()
    }
  }
}
</script>

<style scoped>
.game-trend-mobile {
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  margin-bottom: 16px;
}

.filter-section {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.filter-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.filter-item {
  flex: 1;
  margin-bottom: 0;
}

.filter-label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
}

.mobile-select {
  width: 100%;
}

.mobile-select :deep(.el-input__inner) {
  height: 40px;
  line-height: 40px;
  border-radius: 6px;
  border: 1px solid #dcdfe6;
}

.header-select {
  width: 180px;
  margin-right: 8px;
}

.header-select :deep(.el-input__inner) {
  height: 32px;
  line-height: 32px;
  border-radius: 4px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 12px;
}

.header-select :deep(.el-input__inner)::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.header-select :deep(.el-input__suffix) {
  color: #ffffff;
}

.header-select :deep(.el-select__tags) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
  background: transparent;
  border: none;
  color: #ffffff;
  padding-right: 0;
  height: 20px;
  line-height: 20px;
}

.header-select :deep(.el-tag) {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #ffffff;
  font-size: 10px;
  height: 20px;
  line-height: 18px;
  margin-right: 2px;
  margin-bottom: 0;
  padding: 0 6px;
}

.header-select.type-select {
  width: 120px;
  margin-right: 8px;
}
.header-select.time-select {
  width: 80px;
  margin-right: 8px;
}

@media (max-width: 480px) {
  .header-select {
    width: 160px;
    margin-right: 6px;
  }
  .header-select :deep(.el-input__inner) {
    height: 28px;
    line-height: 28px;
    font-size: 11px;
  }
}


.chart-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  color: white;
}

.card-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.card-actions .el-button {
  color: #ffffff;
  font-size: 16px;
  padding: 4px;
  margin-left: 8px;
}

.card-actions .el-button:hover {
  color: #409EFF;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.chart-container {
  width: 100%;
  height: 350px;
  padding: 0;
  box-sizing: border-box;
}

.select-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  border-bottom: 1px solid #EBEEF5;
  width: 100%;
}

.select-actions .el-button {
  font-size: 12px;
  padding: 4px 8px;
  height: 28px;
  line-height: 20px;
  flex: 1;
  text-align: center;
}

.select-actions .el-divider--vertical {
  height: 12px;
  margin: 0 2px;
}

.mobile-select :deep(.el-select-dropdown__wrap) {
  max-height: 200px;
}

.mobile-select :deep(.el-select-dropdown__item) {
  height: 36px;
  line-height: 36px;
  font-size: 14px;
}

.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-actions {
  display: flex;
  gap: 8px;
}

.dialog-actions .el-button {
  color: #fff;
  padding: 4px;
}

.dialog-actions .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 500px;
  padding: 0;
  box-sizing: border-box;
}

/* 移动端优化 */
@media (max-width: 480px) {
  .game-trend-mobile {
    padding: 0;
    margin-bottom: 12px;
  }
  
  .filter-section {
    padding: 12px;
    margin-bottom: 0;
  }
  
  .filter-row {
    gap: 8px;
  }
  
  .chart-container {
    height: 300px;
    padding: 0;
  }
  
  .card-header {
    padding: 12px 16px;
  }
  
  .card-title {
    font-size: 14px;
  }
  
  .card-actions .el-button {
    font-size: 14px;
    padding: 2px;
    margin-left: 4px;
  }
  
  .fullscreen-chart {
    height: 400px;
    padding: 0;
  }
  

}
</style> 